// Central resolver for logo & static asset URLs.
//
// Strategy: PREFER stable public/ paths for critical branding & default logos.
// If a DB record supplies a *custom* logo_path (admin-uploaded via /api/media/media/…),
// use that. This makes the site resilient to backend restarts / cache misses on
// default branding assets while still supporting admin overrides.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default site branding — always in public/
export const BRAND = {
  logo: "/images/logos/salihi-group.png",
  monogram: "/images/logos/salihi-monogram.png",
  faviconApple: "/images/logos/apple-touch-icon.png",
  favicon16: "/images/logos/favicon-16.png",
  favicon32: "/images/logos/favicon-32.png",
  heroBackground: "/images/site/hero-background.png",
};

// Default subsidiary logos — keyed by slug
const SUBSIDIARY_LOCAL = {
  "salihi-law": "/images/logos/subsidiaries/salihi-law.png",
  "salihi-consulting": "/images/logos/subsidiaries/salihi-consulting.png",
  "salihi-mining": "/images/logos/subsidiaries/salihi-mining.png",
  "salihi-engineering": "/images/logos/subsidiaries/salihi-engineering.png",
  "salihi-investment": "/images/logos/subsidiaries/salihi-investment.png",
  "kaafi-hire": "/images/logos/subsidiaries/kaafi-hire.png",
  "salihi-petroleum": "/images/logos/subsidiaries/salihi-petroleum.png",
};

/**
 * Resolve the logo URL for a subsidiary.
 * Priority:
 *   1. If admin uploaded a custom logo (path starts with /api/media/media/) → use backend URL.
 *   2. Otherwise, use the local /images/logos/subsidiaries/{slug}.png (stable, bundled).
 */
export function subsidiaryLogo(subsidiary) {
  if (!subsidiary) return BRAND.logo;
  const p = subsidiary.logo_path || "";
  if (p.startsWith("/api/media/media/")) return `${BACKEND_URL}${p}`; // admin-uploaded override
  return SUBSIDIARY_LOCAL[subsidiary.slug] || `${BACKEND_URL}${p}`;
}

/**
 * Resolve the logo URL for a client (by slug OR by DB record).
 * Priority:
 *   1. If DB record has admin-uploaded logo_path (/api/media/media/…) → backend.
 *   2. Otherwise → /images/logos/clients/{slug}.png (stable, bundled).
 */
export function clientLogo(clientOrSlug) {
  const slug = typeof clientOrSlug === "string" ? clientOrSlug : clientOrSlug?.slug;
  if (typeof clientOrSlug === "object" && clientOrSlug?.logo_path?.startsWith("/api/media/media/")) {
    return `${BACKEND_URL}${clientOrSlug.logo_path}`;
  }
  return slug ? `/images/logos/clients/${slug}.png` : BRAND.logo;
}

/**
 * Absolute URL for a backend-served media asset (uploaded via admin CMS).
 * Only use for admin-uploaded content (like media library items).
 */
export function backendMedia(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
}

/**
 * Cross-origin friendly onError handler — attempts a same-origin fallback via public/.
 * Useful when the /api/media/ backend hiccups; the image quietly falls back to
 * a bundled default (if one exists at the same filename in /images/logos/*).
 */
export function onLogoError(fallbackPath) {
  return (e) => {
    const img = e.currentTarget;
    if (img.dataset.fallbackApplied === "1") return;
    img.dataset.fallbackApplied = "1";
    if (fallbackPath) img.src = fallbackPath;
    else img.style.visibility = "hidden";
  };
}
